import { validateCNPJ, validateCPF } from 'validations-br';
import { Checkbox, Modal, Radio, Select } from 'antd';
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col } from 'react-bootstrap';

import {
  investments,
  transfer,
  escrow,
  onboarding,
} from '@portal/config/app.json';
import { TaxIdentifierType } from '@portal/enum/onboardingType';
import { BankAccountType } from '@portal/enum/accountType';
import { PixActions, BankActions } from '@portal/actions';
import { PixType } from '@portal/enum/pix';
import { translate } from '@portal/services/i18n';
import {
  isValidCpfOrCnpj,
  maskCnpj,
  maskCpf,
  maskCpfOrCnpj,
  maskPhoneInput,
  unmaskField,
} from '@portal/services/masks';

import ExternalContactForm from '@portal/components/ExternalFavoredForm/ExternalFavoredForm';
import PanelContentHeader from '@portal/components/PanelContentHeader/PanelContentHeader';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import AdvancedForm from '@portal/components/AdvancedForm/AdvancedForm';
import useReduxState from '@portal/hooks/useReduxState';
import { TED_MAXLENGTH_NAME } from '~/config/env';
import { keepOnlyNumbers } from '@portal/utils/format';
import { TransferType } from '@portal/enum/transfers';
import { validateEmail } from '@portal/utils/validate';

interface NewContactModalProps {
  visible: boolean;
  closeModal: () => void;
  onSubmit: (values: models.Contact) => void;
  type: TransferType;
  salaryPortabilityType?: 'TED' | 'PIX';
  title?: string;
  description?: string;
  isSalaryPortabilityFavored?: boolean;
}

const NewContactModal = ({
  closeModal,
  onSubmit,
  visible,
  type,
  title,
  description,
  isSalaryPortabilityFavored = false,
  salaryPortabilityType,
}: NewContactModalProps) => {
  const dispatch = useDispatch();

  const {
    bank: { list: banks },
    user: {
      me: { account },
    },
    pix: { psp, pspError },
  } = useReduxState();

  const [taxIdentifierType, setTaxIdentifierType] = useState(
    TaxIdentifierType.PF
  );
  const [alias, setAlias] = useState('');
  const [taxIdentifier, setTaxIdentifier] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [bankAccountDigit, setBankAccountDigit] = useState('');
  const [bankAgency, setBankAgency] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [bankUserName, setBankUserName] = useState('');
  const [pixKey, setPixKey] = useState('');
  const [pixType, setPixType] = useState(PixType.TAX_IDENTIFIER);
  const [accountType, setAccountType] = useState(BankAccountType.CURRENT);
  const [confirmed, setConfirmed] = useState(false);
  const [pspCode, setPspCode] = useState('');
  const [skipRegistration, setSkipRegistration] = useState(false);
  const [pixKeyError, setPixKeyError] = useState('');

  const getDisabled = (): boolean => {
    switch (type) {
      case TransferType.PIX:
        if (
          (pixType === PixType.BANK_DATA || isSalaryPortabilityFavored) &&
          (bankAccount.trim() === '' ||
            bankAccountDigit.trim() === '' ||
            bankAgency.trim() === '' ||
            String(pspCode ?? '').trim() === '' ||
            !!getErrorMessage())
        ) {
          return true;
        }

        if (
          pixType !== PixType.BANK_DATA &&
          !isSalaryPortabilityFavored &&
          (alias.trim() === '' ||
            pixKey.trim() === '' ||
            !validatePixKeyTaxIdentifier())
        ) {
          return true;
        }

        return false;

      case TransferType.P2P:
        if (taxIdentifier.trim() === '') {
          return true;
        }

        return false;

      case TransferType.TED:
        if (
          alias.trim() === '' ||
          taxIdentifier.trim() === '' ||
          bankCode === '' ||
          bankAccount.trim() === '' ||
          bankAccountDigit.trim() === '' ||
          bankAgency.trim() === ''
        ) {
          return true;
        }

        return false;

      default:
        return false;
    }
  };

  const readonlyRuleForInvestment =
    isSalaryPortabilityFavored || investments.enabled;

  const readOnlyRule = salaryPortabilityType
    ? readonlyRuleForInvestment
    : false;

  const getErrorMessage = (): string | undefined => {
    let validateError = '';
    if (taxIdentifier.length < 11) {
      return;
    }

    if (taxIdentifierType === TaxIdentifierType.PF) {
      validateError = !validateCPF(taxIdentifier)
        ? translate(
            'COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.TAX_IDENTIFIER.PERSON'
          )
        : '';
    } else {
      validateError = !validateCNPJ(taxIdentifier)
        ? translate(
            'COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.TAX_IDENTIFIER.LEGAL'
          )
        : '';
    }

    return validateError
      ? `${validateError} ${translate(
          'COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.TAX_IDENTIFIER.INVALID'
        )}`
      : undefined;
  };

  const handlePixKeyMask = () => {
    switch (pixType) {
      case PixType.PHONE:
        return `+55${unmaskField(pixKey)}`;

      case PixType.TAX_IDENTIFIER:
        return unmaskField(pixKey);

      default:
        return pixKey;
    }
  };

  const validatePixKeyTaxIdentifier = (): boolean => {
    if (pixType !== PixType.TAX_IDENTIFIER || !pixKey) return true;
    const unmaskedKey = unmaskField(pixKey);
    if (unmaskedKey.length < 11) return false;
    return unmaskedKey.length <= 11
      ? validateCPF(unmaskedKey)
      : validateCNPJ(unmaskedKey);
  };

  useEffect(() => {
    if (banks.length === 0 && type === TransferType.TED) {
      dispatch(BankActions.getBanks());
    }
  }, [banks, dispatch, type]);

  useEffect(() => {
    if (psp.length === 0 && type === TransferType.PIX && !pspError) {
      dispatch(PixActions.listPSPs());
    }
  }, [psp, dispatch, type, pspError]);

  useEffect(() => {
    if (readOnlyRule && account.name) {
      setAlias(account.name);
    }
    if (readOnlyRule && account.taxIdentifier) {
      setTaxIdentifier(account.taxIdentifier);
    }
    if (isSalaryPortabilityFavored && account.type) {
      setTaxIdentifierType(account.type as unknown as TaxIdentifierType);
    }
  }, [
    account.name,
    account.taxIdentifier,
    account.type,
    isSalaryPortabilityFavored,
    readOnlyRule,
  ]);

  const handleSubmit = () => {
    if (type === TransferType.TED) {
      onSubmit({
        type: TransferType.TED,
        alias,
        accountType,
        bankAccount,
        bankAccountDigit,
        bankAgency,
        bankCode,
        bankUserName,
        taxIdentifier,
        personType: taxIdentifierType,
        accountFavoredId: undefined,
        pixKey: undefined,
      });
    } else if (type === TransferType.P2P) {
      onSubmit({
        type: TransferType.P2P,
        alias,
        bankAccount: escrow.enabled ? bankAccount : undefined,
        bankAccountDigit: escrow.enabled ? bankAccountDigit : undefined,
        taxIdentifier,
        pixKey: undefined,
      });
    } else {
      onSubmit({
        type: TransferType.PIX,
        alias,
        psp: `${pspCode}`,
        pixKey: handlePixKeyMask(),
        bankAccount,
        bankAccountDigit,
        bankAgency,
        bankUserName,
        taxIdentifier,
        accountFavoredId: undefined,
        accountType,
        skipRegistration,
      });
    }
  };

  const renderName = () => {
    if (type === TransferType.P2P) return null;

    const label = translate(
      type === TransferType.PIX
        ? 'COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.NAME.PIX'
        : `COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.NAME.${
            taxIdentifierType === TaxIdentifierType.PF ? 'PERSON' : 'LEGAL'
          }`
    );

    return (
      <Row>
        <Col className="new-contact-modal__form__input">
          <AdvancedInput
            autoFocus
            variant="standard"
            label={label}
            value={alias}
            onChange={(value) => setAlias(value)}
            readOnly={readOnlyRule}
            helperText
            maxLength={
              onboarding.inputMaxLength && !isNaN(TED_MAXLENGTH_NAME)
                ? TED_MAXLENGTH_NAME
                : undefined
            }
          />
        </Col>
      </Row>
    );
  };

  const renderAccountNumberAndDigit = () =>
    type === TransferType.P2P && escrow.enabled ? (
      <Row>
        <Col className="new-contact-modal__form__input">
          <AdvancedInput
            variant="standard"
            label={translate('COMPONENTS.EXTERNAL_FAVORED_FORM.ACCOUNT')}
            maxLength={20}
            value={bankAccount}
            onChange={(value) => setBankAccount(keepOnlyNumbers(value))}
          />
        </Col>
        <Col className="new-contact-modal__form__input">
          <AdvancedInput
            type="Number"
            variant="standard"
            label={translate('COMPONENTS.EXTERNAL_FAVORED_FORM.DIGIT')}
            maxLength={1}
            value={bankAccountDigit}
            onChange={(value) => {
              if (value.toString().length >= 2) {
                return;
              }
              setBankAccountDigit(value);
            }}
          />
        </Col>
      </Row>
    ) : null;

  const renderTaxIdentifier = () => {
    if (type === TransferType.PIX && !isSalaryPortabilityFavored) {
      return null;
    }

    return (
      <Row>
        <Col className="new-contact-modal__form__input">
          <AdvancedInput
            variant="standard"
            label={translate(
              `COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.TAX_IDENTIFIER.${
                taxIdentifierType === TaxIdentifierType.PF ? 'PERSON' : 'LEGAL'
              }`
            )}
            onChange={(value: string) => setTaxIdentifier(unmaskField(value))}
            value={
              taxIdentifierType === TaxIdentifierType.PF
                ? maskCpf(taxIdentifier)
                : maskCnpj(taxIdentifier)
            }
            error={!!getErrorMessage()}
            helperText={getErrorMessage()}
            readOnly={readOnlyRule}
          />
        </Col>
      </Row>
    );
  };

  const validateTaxIdentifier = (value: string) => {
    if (type === TransferType.PIX && !isSalaryPortabilityFavored) {
      return true;
    }

    return isValidCpfOrCnpj(value);
  };

  const renderTedInfo = () =>
    type === TransferType.TED ||
    (type === TransferType.PIX && isSalaryPortabilityFavored) ? (
      <ExternalContactForm
        banks={banks}
        bankAccount={bankAccount}
        setBankAccount={setBankAccount}
        bankAccountDigit={bankAccountDigit}
        setBankAccountDigit={setBankAccountDigit}
        bankAgency={bankAgency}
        setBankAgency={setBankAgency}
        setBankCode={setBankCode}
        bankCode={bankCode}
        bankUserName={bankUserName}
        setBankUserName={setBankUserName}
        accountType={accountType}
        setAccountType={setAccountType}
        pspList={psp}
        bankCodeType={salaryPortabilityType === 'PIX' ? 'PSP' : 'CODE'}
        setPspCode={setPspCode}
        pspCode={pspCode}
      />
    ) : null;

  const renderPixKey = () => {
    if (type !== TransferType.PIX || isSalaryPortabilityFavored) {
      return null;
    }

    // TODO: Adicionar máscara de chave aleatória
    const maskPixKey = (key: string) => {
      switch (pixType) {
        case PixType.PHONE:
          return maskPhoneInput(key);
        case PixType.TAX_IDENTIFIER:
          return maskCpfOrCnpj(key);
        default:
          return key;
      }
    };

    const cleanState = () => {
      setPixKey('');
      setBankAccount('');
      setBankAccountDigit('');
      setBankAgency('');
      setBankUserName('');
      setTaxIdentifier('');
      setPspCode('');
    };

    return (
      <>
        <Row>
          <Col className="new-contact-modal__form__input">
            <Select
              onChange={(value) => {
                setPixType(value);
                cleanState();
              }}
              className="new-contact-modal__form__input__select"
              bordered={false}
              defaultValue={PixType.TAX_IDENTIFIER}
            >
              {Object.keys(PixType)
                .filter((key) => !Number.isNaN(Number(PixType[key])))
                .map((itPixType) => (
                  <Select.Option value={PixType[itPixType]} key={itPixType}>
                    {translate(
                      `COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.PIX_TYPE.${itPixType}`
                    )}
                  </Select.Option>
                ))}
            </Select>
          </Col>
          {pixType !== PixType.BANK_DATA && (
            <Col className="new-contact-modal__form__input">
              <AdvancedInput
                variant="standard"
                label={translate(
                  'COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.PIX_KEY'
                )}
                onChange={(value: string) => {
                  setPixKey(value);

                  if (pixType === PixType.EMAIL) {
                    if (!validateEmail(value)) {
                      setPixKeyError(
                        translate(
                          'COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.INVALID_MAIL_KEY'
                        )
                      );
                    } else {
                      setPixKeyError('');
                    }
                  } else {
                    setPixKeyError('');
                  }
                }}
                value={maskPixKey(pixKey)}
                helperText={pixKeyError}
              />
            </Col>
          )}
        </Row>
        {pixType === PixType.BANK_DATA && (
          <>
            <Row>
              <Col className="new-contact-modal__form__input">
                <AdvancedInput
                  variant="standard"
                  label={translate(
                    `COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.TAX_IDENTIFIER.${
                      taxIdentifierType === TaxIdentifierType.PF
                        ? 'PERSON'
                        : 'LEGAL'
                    }`
                  )}
                  onChange={(value: string) =>
                    setTaxIdentifier(unmaskField(value))
                  }
                  value={
                    taxIdentifierType === TaxIdentifierType.PF
                      ? maskCpf(taxIdentifier)
                      : maskCnpj(taxIdentifier)
                  }
                  error={!!getErrorMessage()}
                  helperText={getErrorMessage()}
                  readOnly={readOnlyRule}
                />
              </Col>
            </Row>
            <Row>
              <Col className="new-contact-modal__form__input">
                <ExternalContactForm
                  bankAccount={bankAccount}
                  setBankAccount={setBankAccount}
                  bankAccountDigit={bankAccountDigit}
                  setBankAccountDigit={setBankAccountDigit}
                  bankAgency={bankAgency}
                  setBankAgency={setBankAgency}
                  bankUserName={bankUserName}
                  setBankUserName={setBankUserName}
                  accountType={accountType}
                  setAccountType={setAccountType}
                  pspList={psp}
                  bankCodeType={'PSP'}
                  setPspCode={setPspCode}
                  pspCode={pspCode}
                />
              </Col>
            </Row>
          </>
        )}
      </>
    );
  };

  const isSubmitDisabled = () => {
    if (getDisabled()) return true;
    if (getErrorMessage()) return true;
    if (isSalaryPortabilityFavored && !confirmed) return true;
    if (!validateTaxIdentifier(taxIdentifier)) return true;
    if (pixKeyError) return true;

    return false;
  };

  return (
    <Modal
      open={visible}
      footer={null}
      onCancel={closeModal}
      className="new-contact-modal"
    >
      <PanelContentHeader
        pageTitle={
          title ?? translate('COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.TITLE')
        }
        pageDescription={
          description ??
          translate('COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.SUBTITLE')
        }
      />

      <div>
        <AdvancedForm onSubmit={handleSubmit}>
          {(type !== TransferType.PIX || pixType === PixType.BANK_DATA) &&
            !isSalaryPortabilityFavored &&
            !transfer.personalAccount && (
              <>
                <p>
                  {translate(
                    'COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.ACCOUNT_TYPE'
                  )}
                </p>
                <Radio.Group value={taxIdentifierType}>
                  <Radio
                    value={TaxIdentifierType.PF}
                    onClick={() => setTaxIdentifierType(TaxIdentifierType.PF)}
                  >
                    {translate(
                      'COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.TAX_IDENTIFIER.PERSON'
                    )}
                  </Radio>
                  <Radio
                    value={TaxIdentifierType.PJ}
                    onClick={() => setTaxIdentifierType(TaxIdentifierType.PJ)}
                  >
                    {translate(
                      'COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.TAX_IDENTIFIER.LEGAL'
                    )}
                  </Radio>
                </Radio.Group>
              </>
            )}
          {renderName()}
          {renderTaxIdentifier()}
          {renderAccountNumberAndDigit()}
          {renderTedInfo()}
          {renderPixKey()}
          {isSalaryPortabilityFavored ? (
            <Checkbox
              className="new-contact-modal__form__checkbox"
              onChange={(event) => setConfirmed(event.target.checked)}
              checked={confirmed}
            >
              {translate('PAGES.MY_ACCOUNT.SALARY_PORTABILITY.CONFIRM')}
            </Checkbox>
          ) : null}
          <Row className="new-contact-modal__form__button">
            {type === TransferType.PIX && (
              <AdvancedButton
                text={translate(
                  'COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.SKIP_REGISTRATION'
                )}
                onClick={() => setSkipRegistration(true)}
                type="submit"
                disabled={isSubmitDisabled()}
              />
            )}
            <AdvancedButton
              text={translate(
                'COMPONENTS.CONTACT_LIST.NEW_CONTACT.MODAL.SUBMIT'
              )}
              type="submit"
              disabled={isSubmitDisabled()}
            />
          </Row>
        </AdvancedForm>
      </div>
    </Modal>
  );
};

export default NewContactModal;
